import { useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Item, ItemActions, ItemContent } from "@volca/ui/components/ui/item";
import { Stack } from "@volca/ui/components/ui/stack";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import { ChangeStepper } from "@/payroll/editor/change-stepper";
import { ARTIFACT_CONTROLS } from "@/payroll/editor/constants";
import { editorError } from "@/payroll/editor/state";
import type { ComparisonTechnician } from "@/payroll/editor/types";
import {
  useComparisonStatement,
  useComparisonStatements,
} from "./comparison-query";
import { StatementPane } from "./statement-pane";
import { StatementsSkeleton } from "./statements-skeleton";
import { useMirroredScroll } from "./use-mirrored-scroll";

const LINK = "h-auto min-w-0 p-0 text-xs font-medium";

/**
 * One technician's statement, the way they receive it. A statement the proposal
 * cannot render is the loudest thing this view can say, so it says it in place
 * of the document rather than beside it.
 */
export const StatementsView = ({
  path,
  queryKey,
  periodStart,
  enabled,
  note,
}: {
  path: string;
  queryKey: readonly unknown[];
  periodStart: string | undefined;
  enabled: boolean;
  /** Why there is nothing to show, decided once for both lenses. */
  note: string;
}) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [changedOnly, setChangedOnly] = useState(true);
  const rail = useRef<HTMLUListElement>(null);
  const list = useComparisonStatements(path, queryKey, periodStart, enabled);

  const technicians = list.data?.technicians ?? [];
  const changed = technicians.filter((technician) => technician.changed);
  const listed = changedOnly && changed.length ? changed : technicians;
  const active = listed.some((entry) => entry.id === selected)
    ? selected
    : (listed[0]?.id ?? null);
  const detail = useComparisonStatement(path, queryKey, periodStart, active);
  // The same lock the grid uses. Two statements read side by side are only a
  // comparison while they are showing the same part of the document, and these
  // run to a screen or more each: scrolling one and not the other puts a
  // technician's bonus beside somebody's hours.
  const { currentRef, proposedRef } = useMirroredScroll(detail.data);
  // Walking the PayPacks that moved, the way every other pane walks its
  // changes. Without it the only way through 333 people was to read the rail.
  const at = changed.findIndex((entry) => entry.id === active);
  const goTo = (by: number) => {
    if (!changed.length) return;
    const next =
      at < 0
        ? by > 0
          ? 0
          : changed.length - 1
        : (at + by + changed.length) % changed.length;
    setSelected(changed[next].id);
    const node = rail.current?.querySelector(
      `[data-technician="${CSS.escape(changed[next].id)}"]`,
    );
    const viewport = rail.current?.closest<HTMLElement>(
      "[data-scroll-viewport]",
    );
    if (!node || !viewport) return;
    viewport.scrollTop +=
      node.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top -
      viewport.clientHeight / 2;
  };

  // Said by the view that knows which of the four reasons applies. This lens
  // used to tell a reader watching a run in progress to go and start one.
  if (!enabled)
    return (
      <Empty
        className={emptyStateVariants({ layout: "inline", className: "px-4" })}
      >
        <EmptyHeader>
          <EmptyDescription>{note}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  if (list.isPending) return <StatementsSkeleton />;
  if (list.isError)
    return (
      <p className="p-4" role="alert">
        {editorError(list.error)}
      </p>
    );
  if (!list.data?.available)
    return (
      <Empty
        className={emptyStateVariants({ layout: "inline", className: "px-4" })}
      >
        <EmptyHeader>
          <EmptyDescription>{note}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  if (!technicians.length)
    return (
      <Empty className={emptyStateVariants({ layout: "collection" })}>
        <EmptyHeader>
          <EmptyDescription>
            This pay period produced no PayPacks.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );

  return (
    <Stack
      gap={0}
      align="stretch"
      direction="column"
      className="flex min-h-0 flex-1 flex-col"
    >
      {/* The chrome's own band, matching the rows the other panes carry: the
          same grey ground, the same 40px floor. It was white, which read as the
          first line of the PayPack rather than as a control over it. */}
      <div
        className={cn(
          ARTIFACT_CONTROLS,
          "flex-wrap gap-x-3 gap-y-1 border-b-2",
        )}
      >
        {changed.length > 1 && (
          <ChangeStepper
            noun="change"
            label={
              at < 0
                ? `${changed.length} changes`
                : `Change ${at + 1} of ${changed.length}`
            }
            onStep={goTo}
          />
        )}
        <span className="text-subtle">
          {changed.length} of {technicians.length} PayPacks changed
        </span>
        {!!changed.length && changed.length < technicians.length && (
          <Button
            size="xs"
            variant="link"
            className={LINK}
            onClick={() => {
              setChangedOnly(!changedOnly);
              setSelected(null);
            }}
          >
            {changedOnly
              ? `Show all ${technicians.length}`
              : "Only the ones that changed"}
          </Button>
        )}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-[13rem_1fr]">
        <div
          data-scroll-viewport
          className="min-h-0 overflow-y-auto border-r border-line"
        >
          <ul ref={rail} className="py-1">
            {listed.map((technician) => (
              <li key={technician.id} data-technician={technician.id}>
                <Item
                  size="xs"
                  render={<button type="button" />}
                  onClick={() => setSelected(technician.id)}
                  className={cn(
                    "flex-nowrap gap-2 rounded-none px-3 py-1.5 text-left text-xs",
                    {
                      "bg-selected font-medium text-brand hover:bg-selected-hovered":
                        technician.id === active,
                      "text-subtle hover:text-ink": !(technician.id === active),
                    },
                  )}
                >
                  <ItemContent className="min-w-0">
                    <span className="truncate" title={technician.name}>
                      {technician.name}
                    </span>
                  </ItemContent>
                  {!technician.proposed_renders ? (
                    <ItemActions className="shrink-0 text-xs font-medium tracking-widest uppercase">
                      Broken
                    </ItemActions>
                  ) : (
                    technician.changed && (
                      <ItemActions>
                        <span
                          aria-label="Changed"
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent-teal-bold"
                        />
                      </ItemActions>
                    )
                  )}
                </Item>
              </li>
            ))}
          </ul>
        </div>
        {detail.isPending && <StatementsSkeleton body />}
        {detail.isError && (
          <p className="p-4" role="alert">
            {editorError(detail.error)}
          </p>
        )}
        {!!detail.data && (
          <div className="grid min-h-0 grid-cols-2 divide-x divide-line">
            <StatementPane
              heading="Today's rules"
              side={detail.data.current}
              viewportRef={currentRef}
            />
            <StatementPane
              heading="Your proposal"
              side={detail.data.proposed}
              viewportRef={proposedRef}
            />
          </div>
        )}
      </div>
    </Stack>
  );
};

export type { ComparisonTechnician };
